/**
 * abilityVfxMeshes — Default THREE mesh builders by AbilitySpec.shape.
 */
import { buildFrostAura } from './abilityVfxFrostAura.js';
import { buildVolleyArrow } from './abilityVfxVolleyArrow.js';
import { buildHawkshot } from './abilityVfxHawkshot.js';
import { buildCrystalArrow } from './abilityVfxCrystalArrow.js';
import { buildDecisiveStrike } from './abilityVfxDecisiveStrike.js';
import { buildCourage } from './abilityVfxCourage.js';
import { buildJudgment } from './abilityVfxJudgment.js';
import { buildDemacianJustice } from './abilityVfxDemacianJustice.js';
import { buildOverload } from './abilityVfxOverload.js';
import { buildRuneCage } from './abilityVfxRuneCage.js';
import { buildFluxOrb } from './abilityVfxFluxOrb.js';
import { buildDesperatePower } from './abilityVfxDesperatePower.js';
import { buildDisintegrate } from './abilityVfxDisintegrate.js';
import { buildIncinerate } from './abilityVfxIncinerate.js';
import { buildMoltenShield } from './abilityVfxMoltenShield.js';
import { buildTibbersSummon } from './abilityVfxTibbersSummon.js';
import { buildBoomerangBlade } from './abilityVfxBoomerangBlade.js';
import { buildRicochetSpark } from './abilityVfxRicochetSpark.js';
import { buildSpellShield } from './abilityVfxSpellShield.js';
import { buildOnTheHunt } from './abilityVfxOnTheHunt.js';
import { buildFighterAbilityMesh } from './abilityVfxFighterMeshes.js';
import { buildMageAbilityMesh } from './abilityVfxMageMeshes.js';
import { buildSupportAbilityMesh } from './abilityVfxSupportMeshes.js';
import { buildAdcAbilityMesh } from './abilityVfxAdcMeshes.js';
import { buildAssassinAbilityMesh } from './abilityVfxAssassinMeshes.js';
import { buildTankAbilityMesh } from './abilityVfxTankMeshes.js';
import { buildSkirmisherAbilityMesh } from './abilityVfxSkirmisherMeshes.js';

export function buildDefaultAbilityMesh(THREE, spec) {
  const skirmisher = buildSkirmisherAbilityMesh(THREE, spec);
  if (skirmisher) return skirmisher;
  const tank = buildTankAbilityMesh(THREE, spec);
  if (tank) return tank;
  const fighter = buildFighterAbilityMesh(THREE, spec);
  if (fighter) return fighter;
  const mage = buildMageAbilityMesh(THREE, spec);
  if (mage) return mage;
  const support = buildSupportAbilityMesh(THREE, spec);
  if (support) return support;
  const adc = buildAdcAbilityMesh(THREE, spec);
  if (adc) return adc;
  const assassin = buildAssassinAbilityMesh(THREE, spec);
  if (assassin) return assassin;
  const shape = spec.shape;
  if (shape === 'volley_arrow') return buildVolleyArrow(THREE, spec);
  if (shape === 'hawkshot') return buildHawkshot(THREE, spec);
  if (shape === 'crystal_arrow') return buildCrystalArrow(THREE, spec);
  if (shape === 'decisive_strike') return buildDecisiveStrike(THREE, spec);
  if (shape === 'courage') return buildCourage(THREE, spec);
  if (shape === 'judgment') return buildJudgment(THREE, spec);
  if (shape === 'demacian_justice') return buildDemacianJustice(THREE, spec);
  if (shape === 'overload') return buildOverload(THREE, spec);
  if (shape === 'rune_cage') return buildRuneCage(THREE, spec);
  if (shape === 'flux_orb') return buildFluxOrb(THREE, spec);
  if (shape === 'incinerate') return buildIncinerate(THREE, spec);
  if (shape === 'molten_shield') return buildMoltenShield(THREE, spec);
  if (shape === 'tibbers_summon') return buildTibbersSummon(THREE, spec);
  if (shape === 'boomerang_blade') return buildBoomerangBlade(THREE, spec);
  if (shape === 'arrow' || shape === 'bolt' || shape === 'blade' || shape === 'cone_ray') {
    return buildArrowLike(THREE, spec);
  }
  if (shape === 'ult_arrow') return buildUltArrow(THREE, spec);
  if (shape === 'hawk') return buildHawk(THREE, spec);
  if (shape === 'spin') return buildSpin(THREE, spec);
  if (shape === 'beam') return buildBeam(THREE, spec);
  if (shape === 'fireball') return buildFireball(THREE, spec);
  if (shape === 'disintegrate') return buildDisintegrate(THREE, spec);
  if (shape === 'ricochet_spark') return buildRicochetSpark(THREE, spec);
  if (shape === 'spell_shield') return buildSpellShield(THREE, spec);
  if (shape === 'on_the_hunt') return buildOnTheHunt(THREE, spec);
  if (shape === 'frost_aura') return buildFrostAura(THREE, spec);
  if (shape === 'ground_ring') return buildGroundRing(THREE, spec);
  if (spec.id === 'ryze_r' && shape === 'ring') return buildDesperatePower(THREE, spec);
  if (shape === 'shield_ring' || shape === 'ring') return buildRing(THREE, spec);
  return buildSphere(THREE, spec);
}

function matBasic(THREE, spec, opacity = 0.9) {
  return new THREE.MeshBasicMaterial({
    color: spec.color,
    transparent: true,
    opacity,
    side: THREE.DoubleSide
  });
}

function buildArrowLike(THREE, spec) {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color: spec.color });
  const s = 0.85 * (spec.scale || 1);
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04 * s, 0.04 * s, 1.2 * s, 8).rotateX(Math.PI / 2),
    mat
  );
  group.add(shaft);
  const tip = new THREE.Mesh(
    new THREE.ConeGeometry(0.12 * s, 0.4 * s, 6).rotateX(Math.PI / 2),
    mat
  );
  tip.position.z = 0.6 * s;
  group.add(tip);
  return group;
}

function buildUltArrow(THREE, spec) {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color: spec.color });
  const s = spec.scale || 1.6;
  group.add(new THREE.Mesh(
    new THREE.CylinderGeometry(0.18 * s / 1.6, 0.18 * s / 1.6, 3.2 * s / 1.6, 12)
      .rotateX(Math.PI / 2),
    mat
  ));
  return group;
}

function buildHawk(THREE, spec) {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color: spec.color });
  group.add(new THREE.Mesh(new THREE.ConeGeometry(0.35, 1.4, 8).rotateX(Math.PI / 2), mat));
  return group;
}

function buildRing(THREE, spec) {
  const s = 0.45 * (spec.scale || 1);
  const inner = spec.shape === 'shield_ring' ? s * 0.4 : s * 0.55;
  const mesh = new THREE.Mesh(
    new THREE.RingGeometry(inner, s, 24).rotateX(-Math.PI / 2),
    matBasic(THREE, spec, spec.shape === 'shield_ring' ? 0.95 : 0.85)
  );
  mesh.position.set(0, 0.15, 0);
  return mesh;
}

function buildGroundRing(THREE, spec) {
  const s = 0.55 * (spec.scale || 2);
  const mesh = new THREE.Mesh(
    new THREE.RingGeometry(s * 0.35, s, 28).rotateX(-Math.PI / 2),
    matBasic(THREE, spec, 0.8)
  );
  mesh.position.set(0, 0.05, 0);
  mesh.userData = { shape: 'ground_ring' };
  return mesh;
}

function buildSpin(THREE, spec) {
  const group = new THREE.Group();
  const mat = matBasic(THREE, spec, 0.8);
  const s = spec.scale || 1.8;
  for (let i = 0; i < 4; i += 1) {
    const blade = new THREE.Mesh(new THREE.BoxGeometry(s * 1.1, 0.06, 0.22), mat);
    blade.position.y = 0.45;
    if (!blade.rotation) blade.rotation = { y: 0 };
    blade.rotation.y = (Math.PI / 2) * i;
    group.add(blade);
  }
  group.userData = { shape: 'spin' };
  return group;
}

function buildBeam(THREE, spec) {
  const group = new THREE.Group();
  const mat = matBasic(THREE, spec, 0.9);
  const s = spec.scale || 1.3;
  group.add(new THREE.Mesh(
    new THREE.CylinderGeometry(0.08 * s, 0.12 * s, 1, 8).rotateX(Math.PI / 2),
    mat
  ));
  const impact = new THREE.Mesh(new THREE.SphereGeometry(0.35 * s, 10, 10), mat);
  impact.position.z = 0.5;
  group.add(impact);
  group.userData = { shape: 'beam' };
  return group;
}

function buildFireball(THREE, spec) {
  const group = new THREE.Group();
  const mat = matBasic(THREE, spec, 0.92);
  const s = 0.32 * (spec.scale || 1);
  group.add(new THREE.Mesh(new THREE.SphereGeometry(s, 10, 10), mat));
  const flare = new THREE.Mesh(
    new THREE.ConeGeometry(s * 0.7, s * 1.4, 6).rotateX(Math.PI / 2),
    mat
  );
  flare.position.z = -s * 0.6;
  group.add(flare);
  group.userData = { shape: 'fireball' };
  return group;
}

function buildSphere(THREE, spec) {
  const s = 0.4 * (spec.scale || 1);
  return new THREE.Mesh(
    new THREE.SphereGeometry(s, 10, 10),
    matBasic(THREE, spec, 0.9)
  );
}
